import React, { useState, useEffect } from "react";

import { toast } from "react-toastify";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import Dialog from "@material-ui/core/Dialog";
import DialogContent from "@material-ui/core/DialogContent";
import Typography from "@material-ui/core/Typography";
import IconButton from "@material-ui/core/IconButton";
import { i18n } from "../../translate/i18n";
import api from "../../services/api";
import toastError from "../../errors/toastError";
import {
    FormControl as MuiFormControl,
    InputLabel as MuiInputLabel,
    MenuItem as MuiMenuItem,
    Select as MuiSelect,
    Stack
} from "@mui/material";
import { TicketCheck } from "lucide-react";
import ChatiaDialogActions from "../ChatiaDialogActions";
import { CloseXIcon } from "../ChatiaIcons";

const useStyles = makeStyles((theme) => {
    const isDark = theme.mode === "dark" || theme.palette.type === "dark";

    return {
        root: {
            display: "flex",
            flexWrap: "wrap",
        },
        dialogPaper: {
            ...(theme.chatiaDS?.dialogPaper || {}),
            "& .MuiDialogContent-root": {
                flex: 1,
                overflowY: "auto !important",
                overflowX: "hidden",
                background: isDark ? "#121212" : "#ffffff",
                ...theme.scrollbarStyles,
            },
            "& .MuiDialogActions-root": {
                flexShrink: 0,
                padding: "12px 16px",
                background: isDark ? "#121212" : "#ffffff",
            },
        },
        header: {
            ...(theme.chatiaDS?.header || {}),
            flexShrink: 0,
        },
        headerTitle: {
            ...(theme.chatiaDS?.headerTitle || {}),
        },
        closeButton: {
            backgroundColor: theme.chatiaDS?.closeButton?.backgroundColor || "rgba(255,255,255,0.08)",
            color: theme.chatiaDS?.closeButton?.color || "#ffffff",
            transition: theme.chatiaDS?.closeButton?.transition || "all 0.2s ease",
            "&:hover": {
                backgroundColor: theme.chatiaDS?.closeButtonHover?.backgroundColor || "rgba(255,255,255,0.18)",
                color: "#ffffff",
            },
        },
        content: {
            padding: "18px 20px !important",
            maxHeight: "70vh",
        },
        bodyCard: {
            gap: 12,
            display: "flex",
            flexDirection: "column",
        },
        infoCard: {
            background: theme.mode === "dark" ? "#16191f" : "#ffffff",
            color: theme.mode === "dark" ? "#e9edef" : "#111b21",
            border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
            borderRadius: 12,
            padding: 16,
            boxShadow: "0 10px 28px rgba(0, 0, 0, 0.16)",
        },
        cardHeader: {
            display: "flex",
            alignItems: "center",
            gap: 10,
            marginBottom: 14,
            color: theme.mode === "dark" ? "#e9edef" : "#111b21",
            fontSize: 13,
            fontWeight: 700,
            letterSpacing: 0.1,
        },
        cardIcon: {
            width: 32,
            height: 32,
            borderRadius: 10,
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            background: theme.mode === "dark" ? "rgba(255, 255, 255, 0.06)" : "rgba(15, 23, 42, 0.04)",
            border: theme.mode === "dark" ? "1px solid rgba(255, 255, 255, 0.08)" : "1px solid rgba(15, 23, 42, 0.10)",
            color: theme.palette.primary.main,
            "& svg": {
                color: theme.palette.primary.main,
                stroke: theme.palette.primary.main,
            },
        },
    };
});

const FlowBuilderTicketModal = ({
    open,
    onSave,
    data,
    onUpdate,
    close
}) => {
    const classes = useStyles();
    const theme = useTheme();
    const [activeModal, setActiveModal] = useState(false)
    const [queues, setQueues] = useState([])
    const [selectedQueue, setQueueSelected] = useState("")

    useEffect(() => {
        const fetchQueues = async () => {
            try {
                const { data: queueList } = await api.get("/queue");
                setQueues(queueList)
                if (open === "edit") {
                    const queue = queueList.find((item) => item.id === data.data.id)
                    setQueueSelected(queue?.id || "")
                } else {
                    setQueueSelected("")
                }
                setActiveModal(true)
            } catch (error) {
                toastError(error)
            }
        };

        if (open === 'edit') {
            fetchQueues();
        } else if (open === 'create') {
            fetchQueues();
        }
    }, [open]);

    const handleClose = () => {
        close(null)
        setActiveModal(false)
    };

    const handleSaveContact = () => {
        if (!selectedQueue) {
            return toast.error(i18n.t("flowBuilderConfig.modals.ticket.addQueueError"))
        }
        if (open === 'edit') {
            const queue = queues.find(item => item.id === selectedQueue)
            onUpdate({
                ...data,
                data: queue
            });
        } else if (open === 'create') {
            const queue = queues.find(item => item.id === selectedQueue)
            onSave({
                data: queue
            })
        }
        handleClose()

    };

    const title = open === "create"
        ? i18n.t("flowBuilderConfig.modals.ticket.addTitle")
        : i18n.t("flowBuilderConfig.modals.ticket.editTitle");

    const selectFieldSx = {
        mt: "8px",
        "& .MuiOutlinedInput-root": {
            borderRadius: "12px !important",
            minHeight: 40,
            color: theme.mode === "dark" ? "#e9edef" : "#111b21",
        },
        "& .MuiOutlinedInput-notchedOutline": {
            borderRadius: "12px !important",
            borderColor: theme.mode === "dark" ? "rgba(255, 255, 255, 0.18)" : "rgba(15, 23, 42, 0.18)",
        },
        "& .MuiInputBase-root": {
            borderRadius: "12px !important",
        },
        "& .MuiInputLabel-root": {
            backgroundColor: theme.mode === "dark" ? "#16191f" : "#ffffff",
            color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
            px: "4px",
        },
        "& .MuiInputLabel-root.Mui-focused": {
            color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
        },
        "& .MuiSvgIcon-root": {
            color: theme.mode === "dark" ? "#b8c1cc" : "#667781",
        },
    };

    const selectMenuProps = {
        PaperProps: {
            sx: {
                mt: 1,
                borderRadius: "14px",
                boxShadow: "0 10px 36px rgba(0,0,0,0.14)",
                border: "none",
                backgroundImage: "none",
                overflow: "hidden",
                "& .MuiMenu-list": {
                    padding: "6px",
                },
                "& .MuiMenuItem-root": {
                    borderRadius: "8px",
                    padding: "9px 12px",
                    marginBottom: "4px",
                    fontSize: 14,
                    fontWeight: 500,
                    "&.Mui-selected": {
                        background: "rgba(6, 207, 156, 0.10)",
                    },
                    "&.Mui-selected:hover": {
                        background: "rgba(6, 207, 156, 0.16)",
                    },
                },
            },
        },
    };

    const CardTitle = ({ icon: Icon, children }) => (
        <div className={classes.cardHeader}>
            <span className={classes.cardIcon}>
                <Icon size={16} />
            </span>
            <span>{children}</span>
        </div>
    );

    return (
        <div className={classes.root}>
            <Dialog
                open={activeModal}
                onClose={handleClose}
                fullWidth
                maxWidth="md"
                scroll="paper"
                PaperProps={{ className: classes.dialogPaper }}
            >
                <div className={classes.header}>
                    <Typography className={classes.headerTitle}>{title}</Typography>
                    <IconButton size="small" onClick={handleClose} className={classes.closeButton}>
                        <CloseXIcon style={{ fontSize: 18 }} />
                    </IconButton>
                </div>
                <Stack>
                    <DialogContent className={classes.content}>
                        <Stack className={classes.bodyCard}>
                            <Stack className={classes.infoCard}>
                                <CardTitle icon={TicketCheck}>
                                    {title}
                                </CardTitle>
                                <MuiFormControl variant="outlined" fullWidth size="small" sx={selectFieldSx}>
                                    <MuiInputLabel id="ticket-queue-selection-label">
                                        {i18n.t("flowBuilderConfig.modals.ticket.selectConnection")}
                                    </MuiInputLabel>
                                    <MuiSelect
                                        labelId="ticket-queue-selection-label"
                                        id="ticketQueue"
                                        value={selectedQueue}
                                        onChange={(e) => { setQueueSelected(e.target.value) }}
                                        label={i18n.t("flowBuilderConfig.modals.ticket.selectConnection")}
                                        MenuProps={selectMenuProps}
                                    >
                                        {queues.map((queue) => (
                                            <MuiMenuItem key={queue.id} value={queue.id}>
                                                {queue.name}
                                            </MuiMenuItem>
                                        ))}
                                    </MuiSelect>
                                </MuiFormControl>
                            </Stack>
                        </Stack>
                    </DialogContent>
                    <ChatiaDialogActions
                        type="button"
                        onClick={handleSaveContact}
                        saveText={open === 'create' ? i18n.t("flowBuilderConfig.buttons.add") : i18n.t("flowBuilderConfig.buttons.edit")}
                    />
                </Stack>
            </Dialog>
        </div>
    );
};

export default FlowBuilderTicketModal;
